import React, { useRef, cloneElement, useState, useEffect, useCallback, forwardRef, } from 'react';
import { Popper, MenuList, Fade, Box, useForkRef, Backdrop, } from '@mui/material';
import { NestedMenuItem } from './NestedMenuItem';
import { NestedMenuGroup } from './NestedMenuGroup';
import { fadeTimeout, defaultDisablePortal, menuListPositionIdName, menuPopperPositionIdName, } from './constant';
import { getMenuPopperStyle, getMenuWrapperStyle, getBackdropStyle, } from './style';
import { randomStr } from '../../Shared/random';
import { getFirstMenuItemElement, getLastMenuItemElement } from './dom';
import { ScrollView } from '../../ScrollView';
import { useOpenedSubMenuIds, useAnchorElem } from './hooks';
const defaultProps = {
    trigger: 'hover',
    disablePortal: defaultDisablePortal,
    autoFocus: true,
};
export const NestedMenu = forwardRef((props, ref) => {
    const { sx, open, children, items, trigger, selectedKeys = [], onClick: onMenuClick, placement = 'bottom-start', popperProps, menuItemProps, overlayClassName, disablePortal, autoFocus, onClose, disabledTriggerKeys, maxWidth, minWidth, maxHeight, triggerPosition, type = 'radio', backdrop = false, backdropProps, ...restProps } = { ...defaultProps, ...props };
    const childrenProps = children.props;
    const [internalOpen, setInternalOpen] = useState(false);
    const refPopper = useRef(null);
    const hoverLeaveTimer = useRef(null);
    const isHoverTopPopper = useRef(false);
    const needFocusTriggerElement = useRef(false);
    const triggerKey = useRef('');
    const topMenuList = useRef(null);
    const { refOpenedSubMenuIds, onOpenSubMenu, onCloseSubMenu } = useOpenedSubMenuIds();
    const { anchorRef, anchorElem, resetDynamicAnchorElem } = useAnchorElem();
    const menuId = useRef(`dropdown-menu-${randomStr()}`);
    useEffect(() => {
        if (!(open ?? internalOpen)) {
            refOpenedSubMenuIds.current = [];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, internalOpen]);
    const handleOpen = () => {
        setInternalOpen(true);
    };
    const handleClose = () => {
        onClose?.();
        setInternalOpen(false);
    };
    useEffect(() => {
        const topContainer = window;
        const topContainerListener = (e) => {
            if (!(e.target === anchorRef.current ||
                anchorRef.current?.contains(e.target))) {
                if (internalOpen) {
                    handleClose();
                }
            }
        };
        if ((trigger === 'click' || trigger === 'contextMenu') && !backdrop) {
            topContainer.addEventListener('click', topContainerListener);
            topContainer.addEventListener('contextmenu', topContainerListener);
        }
        else {
            topContainer.removeEventListener('click', topContainerListener);
            topContainer.removeEventListener('contextmenu', topContainerListener);
        }
        return () => {
            topContainer.removeEventListener('click', topContainerListener);
            topContainer.removeEventListener('contextmenu', topContainerListener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [internalOpen, onClose, trigger, backdrop]);
    useEffect(() => {
        if (!internalOpen && needFocusTriggerElement.current && autoFocus) {
            const dom = anchorRef?.current;
            dom?.focus();
            needFocusTriggerElement.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [internalOpen, autoFocus]);
    const closeTopParentMenu = (e) => {
        needFocusTriggerElement.current = !!e?.focusTriggerElement;
        handleClose();
    };
    const subItemsCancellerRef = useRef();
    const handleRequestSubItems = useCallback((canceller) => {
        subItemsCancellerRef.current?.();
        subItemsCancellerRef.current = canceller;
    }, []);
    const renderNestedMenu = (options) => {
        const { itemData, parentKeyPath, parentIndexPath, parentPositionPath, index, siblingCount, parentDisabled, closeParentMenu, onRequestSubItems, } = options;
        const { key, children: subItems } = itemData;
        const hasChildren = Array.isArray(subItems) && subItems.length > 0;
        const keyPath = [...[key], ...parentKeyPath];
        const indexPath = [...[index], ...parentIndexPath];
        const positionPath = [...parentPositionPath, ...[index]];
        if (itemData.type === 'group') {
            return (React.createElement(NestedMenuGroup, { type: type, menuId: menuId.current, key: key, index: index, siblingCount: siblingCount, itemData: itemData, parentMenuOpen: hasChildren && (open ?? internalOpen), selectedKeys: selectedKeys, onMenuClick: onMenuClick, keyPath: keyPath, indexPath: indexPath, positionPath: positionPath, closeTopParentMenu: closeTopParentMenu, closeParentMenu: closeParentMenu, renderNestedMenu: renderNestedMenu, popperProps: popperProps, menuItemProps: menuItemProps, overlayClassName: overlayClassName, parentDisabled: parentDisabled, disablePortal: disablePortal, autoFocus: autoFocus, onOpenSubMenu: onOpenSubMenu, onCloseSubMenu: onCloseSubMenu }));
        }
        return (React.createElement(NestedMenuItem, { type: type, menuId: menuId.current, key: key, index: index, siblingCount: siblingCount, itemData: itemData, parentMenuOpen: hasChildren && (open ?? internalOpen), selectedKeys: selectedKeys, onMenuClick: onMenuClick, keyPath: keyPath, indexPath: indexPath, positionPath: positionPath, disablePortal: disablePortal, popperProps: popperProps, menuItemProps: menuItemProps, overlayClassName: overlayClassName, parentDisabled: parentDisabled, closeTopParentMenu: closeTopParentMenu, closeParentMenu: closeParentMenu, autoFocus: autoFocus, onRequestSubItems: onRequestSubItems ?? handleRequestSubItems, maxChildrenWidth: maxWidth, minChildrenWidth: minWidth, maxChildrenHeight: maxHeight, autoLoading: itemData.autoLoading, onOpenSubMenu: onOpenSubMenu, onCloseSubMenu: onCloseSubMenu }, hasChildren
            ? subItems?.map((child, i) => renderNestedMenu({
                type,
                itemData: child,
                parentKeyPath: [...keyPath],
                parentIndexPath: [...indexPath],
                parentPositionPath: [...positionPath],
                index: i,
                siblingCount: subItems.length,
                overlayClassName,
                parentDisabled: itemData.disabled,
                closeTopParentMenu,
                closeParentMenu,
                disablePortal,
            }))
            : null));
    };
    const needFollowDynamicAnchor = () => {
        if (!(trigger === 'click' || trigger === 'contextMenu')) {
            return false;
        }
        if (triggerPosition === 'pointer') {
            return true;
        }
        return false;
    };
    const handleMouseOver = (e) => {
        handleOpen();
        childrenProps?.onMouseOver?.(e);
        restProps?.onMouseOver?.(e);
    };
    const handleMouseLeave = (e) => {
        if (isHoverTopPopper.current) {
            return;
        }
        hoverLeaveTimer.current = window.setTimeout(() => {
            // istanbul ignore if
            if (isHoverTopPopper.current && hoverLeaveTimer.current) {
                window.clearTimeout(hoverLeaveTimer.current);
                return;
            }
            handleClose();
            childrenProps?.onMouseLeave?.(e);
            restProps?.onMouseLeave?.(e);
        }, fadeTimeout);
    };
    const openDropdownByDynamicAnchor = (e) => {
        if (!internalOpen) {
            resetDynamicAnchorElem(e);
            handleOpen();
        }
        else {
            const openedSubMenuIds = [...refOpenedSubMenuIds.current];
            // close existing sub-menus
            subItemsCancellerRef.current?.();
            // close top menu
            closeTopParentMenu();
            if (openedSubMenuIds.length > 0) {
                // wait for fading
                setTimeout(() => {
                    // reset anchor
                    resetDynamicAnchorElem(e);
                    // show dropdown menu again
                    handleOpen();
                }, fadeTimeout);
            }
            else {
                resetDynamicAnchorElem(e);
                handleOpen();
            }
        }
    };
    const handleClick = (e) => {
        if (needFollowDynamicAnchor()) {
            openDropdownByDynamicAnchor(e);
        }
        else if (!Object.keys(props).includes('open') && trigger === 'click') {
            setInternalOpen(!internalOpen);
        }
        else {
            handleOpen();
        }
        childrenProps?.onClick?.(e);
        restProps?.onClick?.(e);
    };
    const handleClickForContextMenu = (e) => {
        if (needFollowDynamicAnchor()) {
            // istanbul ignore else
            if (trigger === 'contextMenu' && internalOpen) {
                handleClose();
            }
        }
        childrenProps?.onClick?.(e);
        restProps?.onClick?.(e);
    };
    const handleContextMenu = (e) => {
        e.preventDefault();
        if (needFollowDynamicAnchor()) {
            openDropdownByDynamicAnchor(e);
        }
        else {
            handleOpen();
        }
        childrenProps?.onContextMenu?.(e);
        restProps?.onContextMenu?.(e);
    };
    const handleKeyDown = (e) => {
        if (Array.isArray(disabledTriggerKeys) &&
            disabledTriggerKeys.includes(e.key)) {
            return;
        }
        triggerKey.current = e.key;
        if (trigger === 'contextMenu') {
            if (e.shiftKey && e.key === 'F10') {
                if (needFollowDynamicAnchor()) {
                    openDropdownByDynamicAnchor();
                }
                else {
                    handleOpen();
                }
            }
        }
        else if (['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
            e.preventDefault();
            if (needFollowDynamicAnchor()) {
                openDropdownByDynamicAnchor();
            }
            else {
                handleOpen();
            }
        }
        childrenProps?.onKeyDown?.(e);
        restProps?.onKeyDown?.(e);
    };
    const handleMenuListKeyDown = (e) => {
        e.stopPropagation();
        if (e.key === 'Tab') {
            needFocusTriggerElement.current = true;
            handleClose();
            // And then it will focus next action DOM by default;
        }
        else {
            e.preventDefault();
            e.stopPropagation();
        }
    };
    const targetComponentProps = {
        'aria-haspopup': 'true',
        'aria-expanded': `${internalOpen}`,
        ...restProps,
        ...childrenProps,
        onKeyDown: handleKeyDown,
        ...(trigger === 'hover' && !needFollowDynamicAnchor()
            ? {
                onMouseOver: handleMouseOver,
                onMouseLeave: handleMouseLeave,
            }
            : {}),
        ...(trigger === 'click'
            ? {
                onClick: handleClick,
            }
            : {}),
        ...(trigger === 'contextMenu'
            ? {
                onClick: handleClickForContextMenu,
                onContextMenu: handleContextMenu,
            }
            : {}),
    };
    const popperComponentProps = {
        role: 'presentation',
        ...(trigger === 'hover'
            ? {
                onMouseOver: () => {
                    isHoverTopPopper.current = true;
                },
                onMouseLeave: () => {
                    isHoverTopPopper.current = false;
                    handleClose();
                },
            }
            : {}),
    };
    const hasSubMenu = items.some((item) => item.type !== 'group' && item.children?.length) ||
        items.some((item) => item.type === 'group' && item.children?.some((t) => t.children?.length));
    // The menu is not scrollable if it has sub menu and the `disablePortal` is `true` because of the overflow: scroll prevents the menu from overflowing.
    const scrollable = !hasSubMenu || !disablePortal;
    const ScrollableAdaptiveContainer = scrollable ? ScrollView : Box;
    return (React.createElement(React.Fragment, null,
        cloneElement(children, {
            ...targetComponentProps,
            ref: useForkRef(anchorRef, children
                .ref, ref),
        }),
        anchorElem &&
            backdrop &&
            (open ?? internalOpen) &&
            trigger !== 'hover' && (React.createElement(Backdrop, { open: open ?? internalOpen, onClick: () => {
                handleClose();
            }, ...backdropProps, sx: [
                ...[(theme) => getBackdropStyle(theme)],
                ...(Array.isArray(backdropProps?.sx)
                    ? backdropProps.sx
                    : [backdropProps?.sx]),
            ] })),
        anchorElem && (React.createElement(Popper, { ...popperProps, ref: refPopper, anchorEl: anchorElem, open: open ?? internalOpen, sx: [
                (theme) => getMenuPopperStyle(theme),
                ...(popperProps && Array.isArray(popperProps?.sx)
                    ? popperProps.sx
                    : [popperProps?.sx]),
            ], disablePortal: disablePortal, autoFocus: true, transition: true, placement: placement, className: overlayClassName, ...popperComponentProps, [menuPopperPositionIdName]: menuId.current, popperOptions: {
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 8],
                        },
                    },
                ],
                onFirstUpdate: () => {
                    if (internalOpen && autoFocus && topMenuList.current) {
                        if (triggerKey.current === 'ArrowUp') {
                            const lastMenuItem = getLastMenuItemElement(topMenuList.current);
                            lastMenuItem?.focus();
                        }
                        else {
                            const firstMenuItem = getFirstMenuItemElement(topMenuList.current);
                            firstMenuItem?.focus();
                        }
                    }
                },
            } }, ({ TransitionProps }) => (React.createElement(Fade, { ...TransitionProps, timeout: fadeTimeout },
            React.createElement(ScrollableAdaptiveContainer, { ...(scrollable
                    ? {
                        orientation: 'vertical',
                    }
                    : {}), style: {
                    maxWidth,
                    minWidth,
                    maxHeight: scrollable ? maxHeight : 'none',
                }, sx: [
                    (theme) => getMenuWrapperStyle(theme, {
                        singleMenu: !items.some((item) => item.children?.length && item.type !== 'group'),
                    }),
                    ...(Array.isArray(sx) ? sx : [sx]),
                ], onClick: (e) => {
                    e.stopPropagation();
                } },
                React.createElement(MenuList, { [menuListPositionIdName]: menuId.current, disablePadding: true, onKeyDown: handleMenuListKeyDown, ref: topMenuList }, items.map((item, i) => renderNestedMenu({
                    type,
                    itemData: item,
                    parentKeyPath: [],
                    parentIndexPath: [],
                    parentPositionPath: [],
                    index: i,
                    siblingCount: items.length,
                    popperProps,
                    menuItemProps,
                    closeTopParentMenu,
                    disablePortal,
                }))))))))));
});
